import React, { useEffect, useState } from "react";
import {
    Card, Typography, Table, Input, Select, Button, Space, Tag,
    Row, Col, Tabs, Empty, DatePicker, Calendar, Avatar, Tooltip,
    message, Popconfirm,
} from "antd";
import {
    PlusOutlined, SearchOutlined, EyeOutlined, EditOutlined, MoreOutlined,
    DownOutlined, ArrowLeftOutlined, DownloadOutlined, FilterOutlined,
    TeamOutlined, CheckCircleOutlined, ClockCircleOutlined, CalendarOutlined,
    CloseCircleOutlined, LeftOutlined, RightOutlined, DeleteOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from "recharts";
import dayjs from "dayjs";
import { MeetingService } from "../../../Services/Manage/MeetingService";
import { UserService } from "../../../Services/Manage/UserService";
import { ProjectService } from "../../../Services/Manage/ProjectService";

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const STATUS_COLORS = {
    Scheduled: "#1677ff",
    "In Progress": "#722ed1",
    Completed: "#52c41a",
    Postponed: "#fa8c16",
    Cancelled: "#f5222d",
};

const MEETING_TYPE_COLORS = {
    "Internal Meeting": "#7c3aed",
    "Client Meeting": "#52c41a",
    "Review Meeting": "#fa8c16",
    "Other Meeting": "#eb2f96",
};
const MEETING_TYPE_PALETTE = ["#7c3aed", "#52c41a", "#fa8c16", "#eb2f96", "#1677ff", "#13c2c2"];

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find((p) => p.link === link) || { add: false, edit: false, delete: false, read: false };
    } catch {
        return { add: false, edit: false, delete: false, read: false };
    }
};

// Horizontal stat card: rounded-square colored icon on the left, label/value/sub stacked on the right
const StatCard = ({ icon, label, value, sub, color }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 120 }}>
        <div
            style={{
                width: 40, height: 40, borderRadius: 10,
                background: `${color}1a`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, color: color || "#1677ff", flexShrink: 0,
            }}
        >
            {icon}
        </div>
        <div>
            <div style={{ fontSize: 12, color: "#888" }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#222", lineHeight: 1.2 }}>{value}</div>
            {sub && <div style={{ fontSize: 11.5, color: color || "#1677ff" }}>{sub}</div>}
        </div>
    </div>
);

const MeetingsList = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [messageApi, contextHolder] = message.useMessage();
    const perm = getPermission("/overview-project/meetings");

    const [userMap, setUserMap] = useState({});
    const [currentUser, setCurrentUser] = useState(null);
    const [projectOptions, setProjectOptions] = useState([]);
    const [isProjectManager, setIsProjectManager] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const user = await UserService.getOmeUser();
                setCurrentUser(user);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);
    const [selectedProjectId, setSelectedProjectId] = useState(location.state?.id || null);

    useEffect(() => {
        (async () => {
            try {
                const res = await UserService.getAllUsers();
                const map = {};
                (res.data || []).forEach((u) => {
                    if (u.user_id) map[String(u.user_id)] = u.name;
                });
                setUserMap(map);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    useEffect(() => {
        if (!perm.view_all && !currentUser) return;
        (async () => {
            try {
                const request = await ProjectService.getAllProject();
                if (request?.status) {
                    let rows = request.data || [];

                    if (!perm.view_all) {
                        const uid = String(currentUser?.uid ?? "").trim();
                        rows = rows.filter((p) => {
                            let engineers = p.pro_engineer;
                            if (typeof engineers === "string") {
                                try { engineers = JSON.parse(engineers); } catch { engineers = []; }
                            }
                            const isEngineer = (engineers || []).some((id) => String(id).trim() === uid);
                            return (
                                String(p.pro_manager ?? "").trim() === uid ||
                                String(p.added_by ?? "").trim() === uid ||
                                isEngineer
                            );
                        });
                    }

                    setProjectOptions(
                        rows.map((p) => ({
                            value: p.id,
                            label: p.pro_name,
                            pro_no: p.pro_no,
                            pro_customer: p.pro_customer,
                            pro_manager: p.pro_manager,
                            pro_value: p.pro_value,
                            pro_start_date: p.pro_start_date,
                            pro_target_end_date: p.pro_target_end_date,
                            pro_status: p.pro_status,
                            pro_engineer: p.pro_engineer,
                        }))
                    );
                }
            } catch (error) {
                console.error(error);
            }
        })();
    }, [currentUser]);

    useEffect(() => {
        if (!selectedProjectId || !currentUser) {
            setIsProjectManager(false);
            return;
        }
        const proj = projectOptions.find((p) => p.value === selectedProjectId);
        const uid = String(currentUser?.uid ?? "").trim();
        setIsProjectManager(!!proj && String(proj.pro_manager ?? "").trim() === uid);
    }, [selectedProjectId, currentUser, projectOptions]);

    const selectedProjectData = projectOptions.find((p) => p.value === selectedProjectId);

    const project = {
        id: selectedProjectId,
        pro_name: selectedProjectData?.label || location.state?.pro_name || "-",
        pro_no: selectedProjectData?.pro_no || location.state?.pro_no || "-",
        customer: selectedProjectData?.pro_customer || location.state?.pro_customer || "-",
        managerId: selectedProjectData?.pro_manager || location.state?.pro_manager || null,
        value: selectedProjectData?.pro_value || location.state?.pro_value || 0,
        engineerIds: selectedProjectData?.pro_engineer || location.state?.pro_engineer || [],
        start_date: (() => {
            const d = selectedProjectData?.pro_start_date || location.state?.pro_start_date;
            return d && dayjs(d).isValid() ? dayjs(d).format("DD-MMM-YYYY") : "-";
        })(),
        end_date: (() => {
            const d = selectedProjectData?.pro_target_end_date || location.state?.pro_target_end_date;
            return d && dayjs(d).isValid() ? dayjs(d).format("DD-MMM-YYYY") : "-";
        })(),
        status: selectedProjectData?.pro_status || location.state?.pro_status || "In Progress",
    };

  const managerName = userMap[String(project.managerId)] || project.managerId || "-";
    const canAddMeeting = perm.add;

    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("all");

    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState(null);
    const [typeFilter, setTypeFilter] = useState(null);
    const [organizerFilter, setOrganizerFilter] = useState(null);
    const [dateRange, setDateRange] = useState([]);

    useEffect(() => {
        if (!perm.view_all && !currentUser) return;
        fetchMeetings();
    }, [selectedProjectId, currentUser]);

    const fetchMeetings = async () => {
        try {
            setLoading(true);
            const request = selectedProjectId
                ? await MeetingService.getAllByProject(selectedProjectId)
                : await MeetingService.getAllMeeting();

            if (request?.status) {
                const rows = (request.data || []).map((m) => {
                    let attendees = m.attendees;
                    if (typeof attendees === "string") {
                        try { attendees = JSON.parse(attendees || "[]"); } catch { attendees = []; }
                    }
                    let optionalAttendees = m.optional_attendees;
                    if (typeof optionalAttendees === "string") {
                        try { optionalAttendees = JSON.parse(optionalAttendees || "[]"); } catch { optionalAttendees = []; }
                    }
                    return {
                        dbId: m.id,
                        meeting_code: m.meeting_code,
                        title: m.meeting_title,
                        meeting_type: m.meeting_type,
                        meeting_date: m.meeting_date,
                        start_time: m.start_time,
                        end_time: m.end_time,
                        organizer: m.organizer,
                        attendees: attendees || [],
                        optional_attendees: optionalAttendees || [],
                        status: m.status,
                        pro_id: m.pro_id,
                        pro_no: m.pro_no,
                        added_by: m.added_by,
                    };
                });
                const visibleRows = perm.view_all
                    ? rows
                    : rows.filter((r) => {
                        const uid = String(currentUser?.uid ?? "").trim();
                        const uname = String(currentUser?.name ?? "").trim();
                        const isOrganizer = uname && r.organizer === uname;
                        const isAttendee = uname && Array.isArray(r.attendees) && r.attendees.includes(uname);
                        const isCreator = uid && String(r.added_by ?? "").trim() === uid;
                        return isOrganizer || isAttendee || isCreator;
                    });
                setMeetings(visibleRows);
            } else {
                setMeetings([]);
            }
        } catch (error) {
            console.error("Error fetching meetings:", error);
            setMeetings([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteMeeting = async (record) => {
        try {
            const request = await MeetingService.deleteMeeting(record.dbId);
            if (request?.status) {
                messageApi.success(request.message || "Meeting deleted");
                fetchMeetings();
            } else {
                messageApi.error(request?.message || "Failed to delete meeting");
            }
        } catch (error) {
            console.error("Error deleting meeting:", error);
            messageApi.error("Failed to delete meeting");
        }
    };

    const goAddMeeting = () =>
        navigate("/overview-project/meetings/add", {
            state: {
                id: project.id,
                pro_name: project.pro_name,
                pro_no: project.pro_no,
                pro_engineer: project.engineerIds,
            },
        });

   const goViewEditMeeting = (record, editable) => {
        navigate("/overview-project/meetings/add", {
            state: {
                id: record.pro_id || project.id,
                pro_name: project.pro_name,
                pro_no: record.pro_no || project.pro_no,
                meetingDbId: record.dbId,
                enableEdit: perm.edit ? editable : false,
                pro_engineer: project.engineerIds,
            },
        });
    };
    const totalCount = meetings.length;
    const completedCount = meetings.filter((m) => m.status === "Completed").length;
    const scheduledCount = meetings.filter((m) => m.status === "Scheduled").length;
    const inProgressCount = meetings.filter((m) => m.status === "In Progress").length;
    const postponedCount = meetings.filter((m) => m.status === "Postponed").length;
    const cancelledCount = meetings.filter((m) => m.status === "Cancelled").length;

    const organizerOptions = [...new Set(meetings.map((m) => m.organizer).filter(Boolean))].map((o) => ({ value: o, label: o }));
    const typeOptions = [...new Set(meetings.map((m) => m.meeting_type).filter(Boolean))].map((t) => ({ value: t, label: t }));

    const filteredData = meetings.filter((m) => {
        const text = searchText.toLowerCase();
        const matchesSearch = !text || (m.title || "").toLowerCase().includes(text);
        const matchesStatus = !statusFilter || m.status === statusFilter;
        const matchesType = !typeFilter || m.meeting_type === typeFilter;
        const matchesOrganizer = !organizerFilter || m.organizer === organizerFilter;

        let matchesDate = true;
        if (dateRange && dateRange.length === 2) {
            const [start, end] = dateRange;
            const d = dayjs(m.meeting_date);
            matchesDate = d.isValid() && d.isBetween ? d.isBetween(start, end, "day", "[]") : true;
        }

        let tabMatch = true;
        const mDate = dayjs(m.meeting_date);
        if (activeTab === "upcoming") tabMatch = mDate.isValid() && !mDate.isBefore(dayjs(), "day") && m.status !== "Completed" && m.status !== "Cancelled";
        if (activeTab === "completed") tabMatch = m.status === "Completed";

        return matchesSearch && matchesStatus && matchesType && matchesOrganizer && matchesDate && tabMatch;
    });

    const upcomingMeetings = [...meetings]
        .filter((m) => dayjs(m.meeting_date).isValid() && !dayjs(m.meeting_date).isBefore(dayjs(), "day"))
        .sort((a, b) => dayjs(a.meeting_date).valueOf() - dayjs(b.meeting_date).valueOf())
        .slice(0, 3);

    const typeCounts = meetings.reduce((acc, m) => {
        if (!m.meeting_type) return acc;
        acc[m.meeting_type] = (acc[m.meeting_type] || 0) + 1;
        return acc;
    }, {});
    const typeDistData = Object.entries(typeCounts).map(([name, value], i) => ({
        name, value, color: MEETING_TYPE_COLORS[name] || MEETING_TYPE_PALETTE[i % MEETING_TYPE_PALETTE.length],
    }));
    const typeDistTotal = typeDistData.reduce((a, b) => a + b.value, 0);

    const eventDatesSet = new Set(meetings.map((m) => dayjs(m.meeting_date).format("YYYY-MM-DD")));
    const dateCellRender = (value) => {
        const key = value.format("YYYY-MM-DD");
        if (!eventDatesSet.has(key)) return null;
        return (
            <div style={{ textAlign: "center", lineHeight: "8px", marginTop: 1 }}>
                <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#1677ff", display: "inline-block" }} />
            </div>
        );
    };

    const renderAttendees = (names, optionalNames) => {
        const required = names || [];
        const optional = optionalNames || [];
        if (required.length === 0 && optional.length === 0) return "-";

        const shown = required.slice(0, 2);
        const overflowNames = [...required.slice(2), ...optional];

        return (
            <Space size={2}>
                {shown.length > 0 && (
                    <Avatar.Group maxCount={2} size="small">
                        {shown.map((n, i) => (
                            <Tooltip key={i} title={n}>
                                <Avatar size="small" style={{ backgroundColor: "#1677ff", fontSize: 11 }}>
                                    {n?.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                                </Avatar>
                            </Tooltip>
                        ))}
                    </Avatar.Group>
                )}
                {overflowNames.length > 0 && (
                    <Tooltip title={overflowNames.join(", ")}>
                        <Avatar size="small" style={{ backgroundColor: "#fa8c16", fontSize: 10 }}>
                            +{overflowNames.length}
                        </Avatar>
                    </Tooltip>
                )}
            </Space>
        );
    };

    const columns = [
        { title: "#", key: "sno", width: 40, align: "center", render: (_, __, i) => i + 1 },
        { title: "Meeting Title", dataIndex: "title", key: "title", width: 150, render: (t) => <Text strong style={{ fontSize: 12.5 }}>{t}</Text> },
        {
            title: "Project", key: "project", width: 130,
            render: (_, record) => {
                const p = projectOptions.find((pr) => pr.value === record.pro_id);
                return <span style={{ fontSize: 12 }}>{p?.label || record.pro_no || "-"}</span>;
            },
        },
        {
            title: "Meeting Type", dataIndex: "meeting_type", key: "meeting_type", width: 110,
            render: (v) => v ? (
                <span style={{
                    background: `${MEETING_TYPE_COLORS[v] || "#8c8c8c"}1a`, color: MEETING_TYPE_COLORS[v] || "#595959",
                    padding: "2px 8px", borderRadius: 6, fontWeight: 600, fontSize: 11,
                }}>{v}</span>
            ) : "-",
        },
        {
            title: "Date & Time", key: "date_time", width: 130,
            render: (_, record) => (
                <div>
                    <div style={{ fontSize: 12 }}>{record.meeting_date ? dayjs(record.meeting_date).format("DD-MMM-YYYY") : "-"}</div>
                    <div style={{ fontSize: 10.5, color: "#888" }}>
                        {record.start_time || "-"}{record.end_time ? ` - ${record.end_time}` : ""}
                    </div>
                </div>
            ),
        },
        { title: "Organizer", dataIndex: "organizer", key: "organizer", width: 100, render: (t) => <span style={{ fontSize: 12 }}>{t || "-"}</span> },
        { title: "Attendees", dataIndex: "attendees", key: "attendees", width: 110, render: (v, record) => renderAttendees(v, record.optional_attendees) },
        {
            title: "Status", dataIndex: "status", key: "status", width: 100,
            render: (v) => (
                <span style={{
                    background: `${STATUS_COLORS[v] || "#bfbfbf"}1a`, color: STATUS_COLORS[v] || "#595959",
                    padding: "2px 8px", borderRadius: 6, fontWeight: 600, fontSize: 11,
                }}>{v || "-"}</span>
            ),
        },
        {
            title: "Action", key: "action", align: "center", width: 100,
            render: (_, record) => (
                <Space size={4}>
                    <Button size="small" icon={<EyeOutlined />} onClick={() => goViewEditMeeting(record, false)} />
                    <Tooltip title={!perm.edit ? "You don't have permission to edit" : ""}>
                        <Button size="small" icon={<EditOutlined />} disabled={!perm.edit} onClick={() => goViewEditMeeting(record, true)} />
                    </Tooltip>
                    <Button size="small" icon={<MoreOutlined />} />
                </Space>
            ),
        },

    ];

    const meetingsTableCard = (
        <Card
            title={<Text strong style={{ fontSize: 15 }}>Meeting List</Text>}
            style={{ marginBottom: 12 }}
            bodyStyle={{ padding: 10 }}
        >
            <Card style={{ marginBottom: 10 }} bodyStyle={{ padding: 8 }}>
                <Row gutter={[8, 8]}>
                    <Col xs={24} sm={12} md={5}>
                        <Input
                            allowClear
                            size="small"
                            placeholder="Search meeting..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Select allowClear size="small" placeholder="All Status" style={{ width: "100%" }}
                            value={statusFilter} onChange={setStatusFilter}
                            options={Object.keys(STATUS_COLORS).map((s) => ({ value: s, label: s }))} />
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Select allowClear size="small" placeholder="All Meeting Type" style={{ width: "100%" }}
                            value={typeFilter} onChange={setTypeFilter}
                            options={typeOptions} />
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Select allowClear size="small" placeholder="All Organizer" style={{ width: "100%" }}
                            value={organizerFilter} onChange={setOrganizerFilter}
                            options={organizerOptions} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <RangePicker allowClear size="small" style={{ width: "100%" }} onChange={(dates) => setDateRange(dates)} />
                    </Col>
                    <Col xs={24} sm={12} md={2}>
                        <Space size={4}>
                            <Button size="small" icon={<FilterOutlined />}>More</Button>
                            <Button size="small" icon={<DownloadOutlined />} />
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Table
                rowKey="dbId"
                dataSource={filteredData}
                columns={columns}
                loading={loading}
                pagination={{ pageSize: 7, size: "small" }}
                size="small"
                scroll={{ x: "max-content" }}
            />
        </Card>
    );

    const placeholderTab = <Card size="small"><Empty description="No meeting minutes yet" /></Card>;

    const tabItems = [
        { key: "all", label: "All Meetings", children: meetingsTableCard },
        { key: "upcoming", label: "Upcoming Meetings", children: meetingsTableCard },
        { key: "completed", label: "Completed Meetings", children: meetingsTableCard },
        { key: "minutes", label: "Meeting Minutes", children: placeholderTab },
    ];

    return (
        <Card bordered={false} bodyStyle={{ padding: 0 }}>
            {contextHolder}
            {/* Header */}
            <div style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div>
                    <Space align="center" size={8}>
                        <ArrowLeftOutlined
                            style={{ cursor: "pointer", fontSize: "18px" }}
                            onClick={() => navigate(-1)}
                        />
                        <Title level={4} style={{ margin: 0, color: "#1658b4" }}>Meetings</Title>
                        <Tag color="green">{project.status}</Tag>
                    </Space>
                </div>
                <Space wrap>
                    {/* <Button size="middle" onClick={() => navigate("/overview-project/view", { state: { id: project.id } })}>
                        View Project
                    </Button> */}
                    <Tooltip title={!canAddMeeting ? "You don't have permission to create" : ""}>
                        <Button type="primary" icon={<PlusOutlined />} onClick={goAddMeeting} disabled={!canAddMeeting}
                            style={canAddMeeting ? { backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" } : undefined}>
                            Add Meeting
                        </Button>
                    </Tooltip>
                    <Button size="middle" icon={<DownOutlined />}>More Actions</Button>
                </Space>
            </div>

            {/* Project selector */}
            <div style={{ padding: "10px 16px 0" }}>
                <Select
                    size="middle"
                    showSearch
                    allowClear
                    placeholder="Select project"
                    style={{ width: 320 }}
                    value={selectedProjectId}
                    onChange={(v) => setSelectedProjectId(v)}
                    options={projectOptions}
                    filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())}
                />
            </div>

            {/* Left column (Project Info + Stats + Tabs) and Right sidebar (Calendar + Meeting Types + Upcoming), single continuous Row */}
            <div style={{ padding: "10px 16px 16px" }}>
                <Row gutter={16} align="start">
                    <Col xs={24} lg={17}>
                        <Card bodyStyle={{ padding: "14px 20px" }}>
                            <Space align="start" size={12} style={{ width: "100%", display: "flex" }}>
                                <div style={{ width: 40, height: 40, borderRadius: 8, background: "#f9f0ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>📁</div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ marginBottom: 8 }}>
                                        <Text strong style={{ fontSize: 15 }}>{project.pro_name}</Text>
                                        <Tag color="cyan" style={{ fontSize: 10, marginLeft: 10 }}>{project.pro_no}</Tag>
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 16, width: "100%" }}>
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Customer</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>{project.customer}</div>
                                        </div>
                                        <div style={{ width: 1, height: 28, background: "#e8e8e8" }} />
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Project Manager</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>{managerName}</div>
                                        </div>
                                        <div style={{ width: 1, height: 28, background: "#e8e8e8" }} />
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Project Value</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>₹{Number(project.value).toLocaleString("en-IN")}</div>
                                        </div>
                                        <div style={{ width: 1, height: 28, background: "#e8e8e8" }} />
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Start Date</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>{project.start_date}</div>
                                        </div>
                                        <div style={{ width: 1, height: 28, background: "#e8e8e8" }} />
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Target End Date</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>{project.end_date}</div>
                                        </div>
                                        <div style={{ width: 1, height: 28, background: "#e8e8e8" }} />
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Status</div>
                                            <Tag color="blue" style={{ fontSize: 11 }}>{project.status}</Tag>
                                        </div>
                                    </div>
                                </div>
                            </Space>
                        </Card>
                        {/* Stat cards — separate card */}
                        <Card style={{ marginTop: 10, marginBottom: 12 }} bodyStyle={{ padding: "16px 20px" }}>
                            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 28, width: "100%" }}>
                                <StatCard label="Total Meetings" value={totalCount} sub="View Details" icon={<TeamOutlined />} color="#7c3aed" />
                                <StatCard label="Completed" value={completedCount} sub={`${totalCount ? Math.round((completedCount / totalCount) * 100) : 0}%`} icon={<CheckCircleOutlined />} color="#52c41a" />
                                <StatCard label="Scheduled" value={scheduledCount} sub={`${totalCount ? Math.round((scheduledCount / totalCount) * 100) : 0}%`} icon={<CalendarOutlined />} color="#fa8c16" />
                                <StatCard label="In Progress" value={inProgressCount} sub={`${totalCount ? Math.round((inProgressCount / totalCount) * 100) : 0}%`} icon={<ClockCircleOutlined />} color="#722ed1" />
                                <StatCard label="Postponed" value={postponedCount} sub={`${totalCount ? Math.round((postponedCount / totalCount) * 100) : 0}%`} icon={<ClockCircleOutlined />} color="#1677ff" />
                                <StatCard label="Cancelled" value={cancelledCount} sub={`${totalCount ? Math.round((cancelledCount / totalCount) * 100) : 0}%`} icon={<CloseCircleOutlined />} color="#f5222d" />
                            </div>
                        </Card>

                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            items={tabItems}
                            style={{ marginTop: 0 }}
                            tabBarStyle={{ marginBottom: 8 }}
                        />
                    </Col>

                    <Col xs={24} lg={7}>
                        <Card
                            title={<Text strong style={{ fontSize: 13 }}>Meeting Calendar</Text>}
                            style={{ marginBottom: 16 }}
                            bodyStyle={{ padding: "4px 6px" }}
                        >
                            <style>{`
                            .meeting-calendar .ant-picker-calendar-header { justify-content: center; gap: 6px; padding: 0px 0 4px; }
                            .meeting-calendar .ant-picker-calendar-header .ant-picker-calendar-year-select,
                            .meeting-calendar .ant-picker-calendar-header .ant-picker-calendar-month-select { display: none; }
                            .meeting-calendar .ant-picker-panel .ant-picker-body { padding: 0px 0px; }
                            .meeting-calendar .ant-picker-cell { padding: 0px 0 !important; }
                            .meeting-calendar .ant-picker-calendar-date { height: 32px !important; width: 28px !important; margin: 0 auto !important; padding: 0 !important; display: flex !important; flex-direction: column !important; align-items: center !important; justify-content: flex-start !important; border-radius: 50% !important; }.meeting-calendar .ant-picker-calendar-date-value { font-size: 11px !important; line-height: 22px !important; width: 22px !important; height: 22px !important; border-radius: 50% !important; display: flex !important; align-items: center !important; justify-content: center !important; margin: 0 auto !important; }                            .meeting-calendar .ant-picker-calendar-date-content { height: 10px !important; line-height: 10px !important; overflow: visible !important; }
                            .meeting-calendar .ant-picker-content thead th { font-size: 9.5px !important; padding-bottom: 2px !important; }
                            .meeting-calendar .ant-picker-cell-selected .ant-picker-calendar-date,
                            .meeting-calendar .ant-picker-cell-today .ant-picker-calendar-date { background: transparent !important; }
                            .meeting-calendar .ant-picker-cell-selected .ant-picker-calendar-date-value { background: #1677ff !important; color: #fff !important; }
                            .meeting-calendar .ant-picker-cell-today .ant-picker-calendar-date-value { border: 1px solid #1677ff !important; }
                            .meeting-calendar .ant-picker-cell-today .ant-picker-calendar-date::before { border: none !important; }
                        `}</style>
                            <div className="meeting-calendar">
                                <Calendar
                                    fullscreen={false}
                                    cellRender={dateCellRender}
                                    headerRender={({ value, onChange }) => (
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "0px 0 2px" }}>
                                            <Button
                                                type="text" size="small" icon={<LeftOutlined style={{ fontSize: 10 }} />}
                                                onClick={() => onChange(value.clone().subtract(1, "month"))}
                                            />
                                            <Text strong style={{ fontSize: 12 }}>{value.format("MMMM YYYY")}</Text>
                                            <Button
                                                type="text" size="small" icon={<RightOutlined style={{ fontSize: 10 }} />}
                                                onClick={() => onChange(value.clone().add(1, "month"))}
                                            />
                                        </div>
                                    )}
                                />
                            </div>
                        </Card>

                        <Card title={<Text strong style={{ fontSize: 15 }}>Meeting Types</Text>} style={{ marginBottom: 16 }} bodyStyle={{ padding: 16 }}>
                            {typeDistData.length === 0 ? (
                                <Empty description="No data" />
                            ) : (
                                <>
                                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                        <div style={{ width: 100, height: 100, flexShrink: 0 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <RechartsTooltip contentStyle={{ fontSize: 11 }} />
                                                    <Pie data={typeDistData} dataKey="value" nameKey="name" innerRadius={28} outerRadius={48} paddingAngle={2}>
                                                        {typeDistData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                                    </Pie>
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            {typeDistData.map((e, i) => (
                                                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12.5, marginBottom: 8 }}>
                                                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: e.color, display: "inline-block" }} />
                                                        {e.name}
                                                    </span>
                                                    <Text strong style={{ fontSize: 12.5 }}>
                                                        {e.value} ({typeDistTotal ? Math.round((e.value / typeDistTotal) * 100) : 0}%)
                                                    </Text>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <Text strong style={{ fontSize: 13, display: "block", marginTop: 8 }}>Total: {typeDistTotal}</Text>
                                </>
                            )}
                        </Card>

                        <Card
                            title={<Text strong style={{ fontSize: 15 }}>Upcoming Meetings</Text>}
                            extra={<a onClick={() => setActiveTab("upcoming")}>View All</a>}
                            bodyStyle={{ padding: 12 }}
                        >
                            {upcomingMeetings.length === 0 && <Empty description="No upcoming meetings" />}
                            {upcomingMeetings.map((m, i) => (
                                <div
                                    key={m.dbId}
                                    onClick={() => goViewEditMeeting(m, false)}
                                    style={{
                                        display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
                                        padding: "8px 0", borderBottom: i !== upcomingMeetings.length - 1 ? "1px solid #f5f5f5" : "none",
                                    }}
                                >
                                    <div style={{
                                        width: 36, textAlign: "center", background: "#f0f5ff", borderRadius: 6, padding: "2px 0", flexShrink: 0,
                                    }}>
                                        <div style={{ fontSize: 10, color: "#1677ff", fontWeight: 600 }}>{dayjs(m.meeting_date).format("MMM")}</div>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: "#1677ff" }}>{dayjs(m.meeting_date).format("DD")}</div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <Text strong style={{ fontSize: 12.5 }}>{m.title}</Text>
                                        <div style={{ fontSize: 11, color: "#888" }}>
                                            {m.start_time}{m.end_time ? ` - ${m.end_time}` : ""}
                                        </div>
                                        <div style={{ fontSize: 11, color: "#888" }}>{m.organizer}</div>
                                    </div>
                                    <CalendarOutlined style={{ color: "#bbb" }} />
                                </div>
                            ))}
                        </Card>
                    </Col>
                </Row>
            </div>
        </Card>
    );
};

export default MeetingsList;